import { IBaseApiRes } from '@interfaces/generic.interface';
import {
  setPOSHasSelectRoleSlice,
  setPOSInitialSelectedRoleSlice,
} from '@modules/point-of-sale/slice/pos-stations.slice';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import {
  PropertyObject,
  UMCreateRolePayloadInterface,
  UMSelfRegPayloadInterface,
  UMUpdateRolePayloadInterface,
} from '../../modules/users-management/interface/users-management.interface';
import { baseQuery } from '../../services/baseQuery';
import { isHydrateAction } from '../../services/hydration';
import { persistKey } from '../../store';
import { handleError } from '../../utils/api-error-handling';
import { encryptPayload } from '../../utils/security';

export const userManagementApi = createApi({
  reducerPath: 'userManagementApi',
  baseQuery: baseQuery,
  extractRehydrationInfo(action, { reducerPath }): any {
    try {
      if (isHydrateAction(action)) {
        if (
          action.key === persistKey &&
          action.payload &&
          action.payload[reducerPath]
        ) {
          return action.payload[reducerPath];
        }
      }
    } catch (error) {
      console.error('Error extracting rehydration info:', error);
    }
    return undefined;
  },
  tagTypes: ['Users', 'User', 'Roles'],
  endpoints: (builder) => ({
    // USERS
    getUsers: builder.query({
      query: () => 'users',
      providesTags: ['Users'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getUser: builder.query({
      query: (params: string | number) => `users/${params}`,
      providesTags: ['User'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    createUser: builder.mutation({
      query: (payload: PropertyObject) => ({
        url: 'users',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          if (data) {
            dispatch(userManagementApi.util.invalidateTags(['Users']));
          }
        } catch (error) {}
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    updateUser: builder.mutation({
      query: (payload: PropertyObject) => ({
        url: 'users',
        method: 'PUT',
        body: encryptPayload(payload),
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          if (data) {
            dispatch(userManagementApi.util.invalidateTags(['Users']));
          }
        } catch (error) {}
      },
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    selfRegistration: builder.mutation({
      query: (payload: UMSelfRegPayloadInterface) => ({
        url: 'users?action[selfRegistration]=true',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    verifyCreds: builder.mutation({
      query: (payload: {
        userID: string;
        securityCode?: string;
        password?: string;
        signature: string;
      }) => ({
        url: 'credentials/verify',
        method: 'POST',
        body: encryptPayload(payload),
      }),
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    // DASHBOARD
    getDashboardDataWithinToken: builder.query({
      query: () => 'dashboards/usersManagement',
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    // ROLES
    getRoles: builder.query({
      query: () => 'roles',
      providesTags: ['Roles'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getRole: builder.query({
      query: (params: string | number) => `roles/${params}`,
      providesTags: ['Roles'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    createRole: builder.mutation({
      query: (payload: UMCreateRolePayloadInterface) => ({
        // url: "roles/create", // roles/
        url: 'roles', // roles/
        method: 'POST',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['Roles'],
      transformErrorResponse: (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    updateRole: builder.mutation({
      query: (payload: UMUpdateRolePayloadInterface) => ({
        url: 'roles',
        method: 'PUT',
        body: encryptPayload(payload),
      }),
      invalidatesTags: ['Roles'],
      transformErrorResponse: (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),

    // PRIVILEGES
    getUserRolePrivilege: builder.query({
      query: (params: string | number) => `roles/${params}/priviledges`,
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getRolePrivilege: builder.query({
      query: () => `roles/blank/priviledges`,
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    getUserPrivilege: builder.query({
      query: ({ roleID }: { roleID: string; refetch?: string }) =>
        `privileges/roles${roleID ? `/${roleID}` : ''}`,
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        const { data } = await queryFulfilled;

        const isCashier =
          data?.privileges?.some(
            (priv) =>
              priv.priv_code === 'log_as_cashier' && Boolean(priv.has_access),
          ) ?? false;

        dispatch(setPOSHasSelectRoleSlice(isCashier));
        dispatch(
          setPOSInitialSelectedRoleSlice(isCashier ? 'Cashier' : 'Waiter'),
        );
      },
      forceRefetch: () => true,
      transformResponse: (
        response: IBaseApiRes<{
          privileges: { priv_code: string; has_access: number }[];
        }>,
      ) => response?.data,
      transformErrorResponse: (baseQueryReturnValue) =>
        handleError(baseQueryReturnValue),
    }),
    // DEVICES
    getDevices: builder.query({
      query: (params: string | number) => `users/devices/${params}`,
      providesTags: ['User'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
    deleteDevices: builder.mutation({
      query: (params: string | number) => ({
        url: `users/devices/${params}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['User'],
      transformErrorResponse: async (baseQueryReturnValue: any) => {
        return handleError(baseQueryReturnValue);
      },
    }),
  }),
});

export const {
  // USERS
  useGetUsersQuery,
  useGetUserQuery,
  useLazyGetUserQuery,

  useCreateUserMutation,
  useUpdateUserMutation,
  useSelfRegistrationMutation,
  useVerifyCredsMutation,

  // DASHBOARD
  useGetDashboardDataWithinTokenQuery,

  // ROLES
  useGetRolesQuery,
  useLazyGetRolesQuery,
  useGetRoleQuery,
  useLazyGetRoleQuery,

  useCreateRoleMutation,
  useUpdateRoleMutation,

  // PRIVILEGES
  useGetUserRolePrivilegeQuery,
  useLazyGetUserRolePrivilegeQuery,
  useGetRolePrivilegeQuery,
  useGetUserPrivilegeQuery,

  // DEVICES
  useGetDevicesQuery,
  useLazyGetDevicesQuery,
  useDeleteDevicesMutation,
} = userManagementApi;
